import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { SiteConfigProvider } from '@byaft/ui/seo';
import { SiteHeader } from './components/SiteHeader';
import { SiteFooter } from './components/SiteFooter';
import { CookieConsent } from './components/CookieConsent';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Home } from './pages/Home';
import { About } from './pages/About';
import { Privacy } from './pages/Privacy';
import { BRAND_NAME, SITE_URL } from './siteConfig';

/**
 * Scroll to top on route change (small site, no scroll restoration needed).
 *
 * `behavior: 'instant'` is load-bearing: the base stylesheet sets
 * `html { scroll-behavior: smooth }`, so a plain `scrollTo(0, 0)` *animates* — and
 * that animation is immediately clobbered by the film's Lenis rAF loop, leaving the
 * new page parked at the old scroll offset (i.e. deep inside the film's scrub range,
 * showing an empty frame between beats).
 */
function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Block body on purpose. `useEffect(() => expr, deps)` hands `expr`'s return
    // value to React as the cleanup function — and React only guards it against
    // `undefined`, so anything else (null, a number, a Promise) is later invoked
    // and throws "destroy is not a function" during the passive-effect unmount on
    // the *next* navigation. Nothing here may return a value.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

export function App() {
  return (
    <SiteConfigProvider siteUrl={SITE_URL} brandName={BRAND_NAME}>
      {/* Outer boundary: the site chrome (header/footer/consent/ScrollTop) sits
          outside the routed content, so a throw there has no boundary above it and
          React tears down the whole root — a totally black page. This guarantees a
          readable fallback instead. The inner boundary is preferred whenever it can
          catch, since it keeps the header and footer on screen. */}
      <ErrorBoundary>
        <div className="noise-overlay" aria-hidden="true" />
        <ScrollTop />
        <SiteHeader />
        <main id="main">
          <ErrorBoundary>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ErrorBoundary>
        </main>
        <SiteFooter />
        <CookieConsent />
      </ErrorBoundary>
    </SiteConfigProvider>
  );
}
